import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Button, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { useNotification } from "../../providers/NotificationProvider";
import { fetchMemberType } from "../../api/memberApi";

function DetailRow({ label, value }) {
  return (
    <Box sx={{ display: "flex", py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
      <Typography variant="body2" sx={{ width: 200, flexShrink: 0, color: "text.secondary", fontWeight: 500 }}>{label}</Typography>
      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{value || "—"}</Typography>
    </Box>
  );
}

function SubTable({ title, headers, rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{title}</Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "grey.50" }}>
              {headers.map((h) => <TableCell key={h} sx={{ fontWeight: 600 }}>{h}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((cells, i) => (
              <TableRow key={i}>
                {cells.map((c, j) => <TableCell key={j} sx={{ whiteSpace: "pre-wrap" }}>{c || "—"}</TableCell>)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default function MemberTypeDetailPage() {
  const { businessDomainId, memberTypeId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchMemberType(memberTypeId);
      setItem(data);
    } catch (e) { notify.apiError(e); }
    finally { setLoading(false); }
  }, [memberTypeId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [loadData]);

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}><CircularProgress /></Box>;
  if (!item) return null;

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/design/${businessDomainId}/member-types`)} variant="text">
          {t("common.back")}
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>{t("memberType.detail.title")}</Typography>
      </Box>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <DetailRow label={t("common.name")} value={item.memberTypeName} />
        <DetailRow label={t("common.description")} value={item.memberTypeDescription} />
      </Paper>
      <SubTable
        title={t("type.indicators")}
        headers={[t("type.indicator.name"), t("type.indicator.unit"), t("type.indicator.description")]}
        rows={(item.indicators || []).map((ind) => [ind.indicatorName, ind.unit, ind.description])}
      />
      <SubTable
        title={t("type.criteria")}
        headers={[t("type.criteria.code"), t("type.criteria.name")]}
        rows={(item.classificationCriterias || []).map((c) => [c.classificationCriteriaCd, c.classificationCriteriaName])}
      />
      <SubTable
        title={t("type.criteriaValues")}
        headers={[t("type.criteria.name"), t("type.criteriaValue.name")]}
        rows={(item.classificationCriteriaValues || []).map((v) => [v.classificationCriteria?.classificationCriteriaName, v.classificationCriteriaValueName])}
      />
    </Box>
  );
}
